import type {
  CanonicalCompatibilityEvidence,
} from '../tree/CanonicalTreeCompatibilityEdgeProjection';

import {
  sanitizeTreeRuntimeMissingParentReferenceLifecycleEvidence,
  type TreeRuntimeMissingParentReferenceLifecycleEvidence,
} from './TreeRuntimeDiscrepancyCohortLifecycleEvidence';

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE =
  'CC-TREE-LIVE-003G2P' as const;

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_RPC =
  'observe_tree_missing_parent_identity_parity' as const;

export const TREE_RUNTIME_MISSING_PARENT_COMMITMENT_VERSION =
  'KC_TREE_MISSING_PARENT_REFERENCE_PAIR_V1' as const;

export const TREE_RUNTIME_MISSING_PARENT_COMMITMENT_ALGORITHM =
  'SHA-256' as const;

export const TREE_RUNTIME_MISSING_PARENT_REASON =
  'parent_ids references a member that is not present in the loaded graph.' as const;

export const TREE_RUNTIME_MISSING_PARENT_IDENTITY_SQL_AUTHORITY =
  Object.freeze({
    securityInvoker: true,
    rlsBound: true,
    derivesActorFromAuthUid: true,
    derivesEffectiveOwnerServerSide: true,
    derivesReferencesServerSide: true,
    acceptsCandidateIds: false,
    acceptsRawMemberIds: false,
    returnsRawMemberIds: false,
    identitySuppressed: true,
    readOnly: true,
    repairExecuted: false,
  } as const);

export interface TreeRuntimeMissingParentReferencePair {
  readonly childId: string;
  readonly parentId: string;
}

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const validUuid =
  (value: unknown): value is string =>
    typeof value === 'string' &&
    UUID.test(value);

const normalizeUuid =
  (value: string): string =>
    value.toLowerCase();

export function deriveTreeRuntimeMissingParentReferencePairs(
  evidence:
    readonly CanonicalCompatibilityEvidence[],
): readonly TreeRuntimeMissingParentReferencePair[] | null {
  const pairs =
    new Map<
      string,
      TreeRuntimeMissingParentReferencePair
    >();

  for (const item of evidence) {
    if (
      item.source !== 'parent_ids' ||
      item.reason !==
        TREE_RUNTIME_MISSING_PARENT_REASON
    ) {
      continue;
    }

    if (
      !validUuid(item.sourceReferenceId) ||
      item.memberIds.length !== 2 ||
      !validUuid(item.memberIds[0]) ||
      !validUuid(item.memberIds[1])
    ) {
      return null;
    }

    const childId =
      normalizeUuid(item.memberIds[0]);

    const parentId =
      normalizeUuid(item.memberIds[1]);

    if (
      childId !==
      normalizeUuid(item.sourceReferenceId)
    ) {
      return null;
    }

    const key =
      `${childId}|${parentId}`;

    pairs.set(
      key,
      Object.freeze({
        childId,
        parentId,
      }),
    );
  }

  return Object.freeze(
    [...pairs.values()]
      .sort((a, b) => {
        if (a.childId < b.childId) return -1;
        if (a.childId > b.childId) return 1;
        if (a.parentId < b.parentId) return -1;
        if (a.parentId > b.parentId) return 1;
        return 0;
      }),
  );
}

export function serializeTreeRuntimeMissingParentReferencePairs(
  pairs:
    readonly TreeRuntimeMissingParentReferencePair[],
): string {
  return [
    TREE_RUNTIME_MISSING_PARENT_COMMITMENT_VERSION,
    `count=${pairs.length}`,
    ...pairs.map(
      pair =>
        `${pair.childId}|${pair.parentId}`,
    ),
  ].join('\n');
}

const validNonce =
  (value: unknown): value is string =>
    typeof value === 'string' &&
    /^[0-9a-f]{32}$/.test(value);

export function createTreeRuntimeMissingParentCommitmentNonce():
  string | null {
  const cryptoAuthority =
    globalThis.crypto;

  if (!cryptoAuthority?.getRandomValues) {
    return null;
  }

  const bytes =
    new Uint8Array(16);

  cryptoAuthority.getRandomValues(bytes);

  return [...bytes]
    .map(
      byte =>
        byte
          .toString(16)
          .padStart(2, '0'),
    )
    .join('');
}

export async function createTreeRuntimeMissingParentIdentityCommitment(
  input: {
    readonly nonce: string;
    readonly pairs:
      readonly TreeRuntimeMissingParentReferencePair[];
  },
): Promise<string | null> {
  if (!validNonce(input.nonce)) {
    return null;
  }

  const subtle =
    globalThis.crypto?.subtle;

  if (!subtle) {
    return null;
  }

  const payload =
    [
      input.nonce,
      serializeTreeRuntimeMissingParentReferencePairs(
        input.pairs,
      ),
    ].join('\n');

  const digest =
    await subtle.digest(
      TREE_RUNTIME_MISSING_PARENT_COMMITMENT_ALGORITHM,
      new TextEncoder().encode(payload),
    );

  return [...new Uint8Array(digest)]
    .map(
      byte =>
        byte
          .toString(16)
          .padStart(2, '0'),
    )
    .join('');
}

export type TreeRuntimeMissingParentIdentityParityRpcStatus =
  | 'MATCH'
  | 'MISMATCH';

export interface TreeRuntimeMissingParentIdentityParityRpcResponse {
  readonly ok: true;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
  readonly identitySuppressed: true;
  readonly candidateIdsAccepted: false;
  readonly commitmentAlgorithm:
    typeof TREE_RUNTIME_MISSING_PARENT_COMMITMENT_ALGORITHM;
  readonly commitmentVersion:
    typeof TREE_RUNTIME_MISSING_PARENT_COMMITMENT_VERSION;
  readonly status:
    TreeRuntimeMissingParentIdentityParityRpcStatus;
  readonly identityParityProven: boolean;
  readonly serverMissingReferencePairsCount: number;
  readonly missingParentReferenceLifecycle:
    TreeRuntimeMissingParentReferenceLifecycleEvidence;
}

const RESPONSE_FIELDS =
  Object.freeze([
    'ok',
    'readOnly',
    'repairExecuted',
    'repairReady',
    'identitySuppressed',
    'candidateIdsAccepted',
    'commitmentAlgorithm',
    'commitmentVersion',
    'status',
    'identityParityProven',
    'serverMissingReferencePairsCount',
    'missingParentReferenceLifecycle',
  ] as const);

const validCount =
  (value: unknown): value is number =>
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 1_000_000;

export function sanitizeTreeRuntimeMissingParentIdentityParityRpcResponse(
  candidate: unknown,
): TreeRuntimeMissingParentIdentityParityRpcResponse | null {
  if (
    !candidate ||
    typeof candidate !== 'object'
  ) {
    return null;
  }

  const value =
    candidate as Record<string, unknown>;

  const keys =
    Object.keys(value).sort();

  const expected =
    [...RESPONSE_FIELDS].sort();

  if (
    keys.length !== expected.length ||
    !keys.every(
      (key, index) =>
        key === expected[index],
    )
  ) {
    return null;
  }

  if (
    value.ok !== true ||
    value.readOnly !== true ||
    value.repairExecuted !== false ||
    value.repairReady !== false ||
    value.identitySuppressed !== true ||
    value.candidateIdsAccepted !== false ||
    value.commitmentAlgorithm !==
      TREE_RUNTIME_MISSING_PARENT_COMMITMENT_ALGORITHM ||
    value.commitmentVersion !==
      TREE_RUNTIME_MISSING_PARENT_COMMITMENT_VERSION ||
    !validCount(
      value.serverMissingReferencePairsCount,
    )
  ) {
    return null;
  }

  if (
    value.status !== 'MATCH' &&
    value.status !== 'MISMATCH'
  ) {
    return null;
  }

  if (
    typeof value.identityParityProven !==
      'boolean' ||
    value.identityParityProven !==
      (value.status === 'MATCH')
  ) {
    return null;
  }

  const lifecycle =
    sanitizeTreeRuntimeMissingParentReferenceLifecycleEvidence(
      value.missingParentReferenceLifecycle,
    );

  if (!lifecycle) {
    return null;
  }

  return Object.freeze({
    ok: true,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
    identitySuppressed: true,
    candidateIdsAccepted: false,
    commitmentAlgorithm:
      TREE_RUNTIME_MISSING_PARENT_COMMITMENT_ALGORITHM,
    commitmentVersion:
      TREE_RUNTIME_MISSING_PARENT_COMMITMENT_VERSION,
    status:
      value.status,
    identityParityProven:
      value.identityParityProven,
    serverMissingReferencePairsCount:
      value.serverMissingReferencePairsCount,
    missingParentReferenceLifecycle:
      lifecycle,
  });
}

export type TreeRuntimeMissingParentRepairReadinessStatus =
  | 'NO_REPAIR_REQUIRED'
  | 'BLOCKED_IDENTITY_UNPROVEN'
  | 'BLOCKED_COUNT_MISMATCH'
  | 'BLOCKED_SERVER_LIFECYCLE_AUTHORITY'
  | 'BLOCKED_INDETERMINATE'
  | 'ELIGIBLE_FOR_REPAIR_DESIGN';

export type TreeRuntimeMissingParentIdentityParityDecisionStatus =
  | 'IDENTITY_PARITY_PROVEN'
  | 'IDENTITY_PARITY_MISMATCH'
  | 'INVALID_SERVER_RESPONSE'
  | 'SERVER_BROWSER_COUNT_MISMATCH'
  | 'SERVER_LIFECYCLE_AUTHORITY_VIOLATION';

export interface TreeRuntimeMissingParentIdentityParityDecision {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE;
  readonly status:
    TreeRuntimeMissingParentIdentityParityDecisionStatus;
  readonly identityParityProven: boolean;
  readonly browserMissingReferencePairsCount: number;
  readonly serverMissingReferencePairsCount:
    number | null;
  readonly missingParentReferenceLifecycle:
    TreeRuntimeMissingParentReferenceLifecycleEvidence | null;
  readonly repairReadinessStatus:
    TreeRuntimeMissingParentRepairReadinessStatus;
  readonly repairPlanningEligible: boolean;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
}

export function resolveTreeRuntimeMissingParentIdentityParity(
  input: {
    readonly browserMissingReferencePairsCount: number;
    readonly response: unknown;
  },
): TreeRuntimeMissingParentIdentityParityDecision {
  const rejected = (
    status:
      TreeRuntimeMissingParentIdentityParityDecisionStatus,
    repairReadinessStatus:
      TreeRuntimeMissingParentRepairReadinessStatus,
    serverCount: number | null = null,
    lifecycle:
      TreeRuntimeMissingParentReferenceLifecycleEvidence | null =
        null,
  ): TreeRuntimeMissingParentIdentityParityDecision =>
    Object.freeze({
      milestone:
        TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE,
      status,
      identityParityProven: false,
      browserMissingReferencePairsCount:
        input.browserMissingReferencePairsCount,
      serverMissingReferencePairsCount:
        serverCount,
      missingParentReferenceLifecycle:
        lifecycle,
      repairReadinessStatus,
      repairPlanningEligible: false,
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
    });

  if (
    !validCount(
      input.browserMissingReferencePairsCount,
    )
  ) {
    return rejected(
      'INVALID_SERVER_RESPONSE',
      'BLOCKED_IDENTITY_UNPROVEN',
    );
  }

  const response =
    sanitizeTreeRuntimeMissingParentIdentityParityRpcResponse(
      input.response,
    );

  if (!response) {
    return rejected(
      'INVALID_SERVER_RESPONSE',
      'BLOCKED_IDENTITY_UNPROVEN',
    );
  }

  const lifecycle =
    response.missingParentReferenceLifecycle;

  if (
    lifecycle
      .missingParentReferenceActiveSameGraphCount !== 0 ||
    lifecycle
      .missingParentReferenceCrossGraphCount !== 0 ||
    lifecycle
      .missingParentReferencePhysicallyAbsentCount !== 0
  ) {
    return rejected(
      'SERVER_LIFECYCLE_AUTHORITY_VIOLATION',
      'BLOCKED_SERVER_LIFECYCLE_AUTHORITY',
      response.serverMissingReferencePairsCount,
      lifecycle,
    );
  }

  const classifiedCount =
    lifecycle
      .missingParentReferenceSoftDeletedSameGraphCount +
    lifecycle
      .missingParentReferenceIndeterminateCount;

  if (
    classifiedCount !==
      response.serverMissingReferencePairsCount ||
    response.serverMissingReferencePairsCount !==
      input.browserMissingReferencePairsCount
  ) {
    return rejected(
      'SERVER_BROWSER_COUNT_MISMATCH',
      'BLOCKED_COUNT_MISMATCH',
      response.serverMissingReferencePairsCount,
      lifecycle,
    );
  }

  if (!response.identityParityProven) {
    return rejected(
      'IDENTITY_PARITY_MISMATCH',
      'BLOCKED_IDENTITY_UNPROVEN',
      response.serverMissingReferencePairsCount,
      lifecycle,
    );
  }

  if (
    response.serverMissingReferencePairsCount ===
      0
  ) {
    return Object.freeze({
      milestone:
        TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE,
      status: 'IDENTITY_PARITY_PROVEN',
      identityParityProven: true,
      browserMissingReferencePairsCount: 0,
      serverMissingReferencePairsCount: 0,
      missingParentReferenceLifecycle:
        lifecycle,
      repairReadinessStatus:
        'NO_REPAIR_REQUIRED',
      repairPlanningEligible: false,
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
    });
  }

  const indeterminate =
    lifecycle
      .missingParentReferenceIndeterminateCount;

  const repairPlanningEligible =
    indeterminate === 0 &&
    lifecycle
      .missingParentReferenceSoftDeletedSameGraphCount ===
      response.serverMissingReferencePairsCount;

  return Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE,
    status:
      'IDENTITY_PARITY_PROVEN',
    identityParityProven: true,
    browserMissingReferencePairsCount:
      input.browserMissingReferencePairsCount,
    serverMissingReferencePairsCount:
      response.serverMissingReferencePairsCount,
    missingParentReferenceLifecycle:
      lifecycle,
    repairReadinessStatus:
      repairPlanningEligible
        ? 'ELIGIBLE_FOR_REPAIR_DESIGN'
        : 'BLOCKED_INDETERMINATE',
    repairPlanningEligible,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });
}
